import { dispatch, getState } from "../../state/store";
import {
  setBatteryHealthEvents,
  setConnectionTimeEvents,
  setConnectionsByMarket,
  setDeviceUsageEvents,
  setDiagnosticsByMarket,
  setDiagnosticsEvents,
  setFwuByMarket,
  setFwuEvents,
  setPageLoadsEvents,
  setReconnectionTimeEvents,
  setConnectionsByBrowser,
  setConnectionsByOS,
  setDiagnosticsErrorsReadingPerfomanceEvents,
  setSettingEvents
} from "../../state/ducks";
import { selectSelectedMarket } from "../../state/selectors";

import { chartNames, dashboardGroups, rumEvents, settingNames } from "../../consts";

import {
  dashboardUtil,
  batteryHealthDashboardUtil,
  diagnosticDashboardUtil,
  firmwareDashboardUtil,
  overviewDashboardUtil,
  settingsDashboardUtil,
  browserAndOSUtil,
  appsUtil,
  eventAppsUtil
} from "../../utils";

const fetchEventsConfig = {
  [rumEvents.PERFORMANCE_NAVIGATION_EVENT]: {
    chartNames: [chartNames.PAGE_LOADS, chartNames.AVG_PAGE_LOAD_SPEED],
    group: dashboardGroups.OVERVIEW,
    saveDataFn: (data) => {
      const selectedApps = appsUtil.getSelectedApps();

      const appsWithPageLoads = eventAppsUtil.appsWithPageLoads(selectedApps);

      const {
        result: loads,
        totalPageLoads,
        totalDuration
      } = overviewDashboardUtil.getPageLoads(
        data.filter((d) => appsWithPageLoads.some((a) => a.appType === d.appType))
      );
      dispatch(setPageLoadsEvents({ loads, total: totalPageLoads, avgSpeed: totalDuration }));
    }
  },
  [rumEvents.CONNECT_DEVICE]: {
    chartNames: [chartNames.CONNECTION_TIME, chartNames.IQOS_DEVICE_USAGE],
    deviceFilterPatternFn: dashboardUtil.getFilterPatternByDeviceName,
    group: dashboardGroups.OVERVIEW,
    saveDataFn: (data) => {
      const selectedApps = appsUtil.getSelectedApps();

      const appsWithConnectionTime = eventAppsUtil.appsWithConnectionTime(selectedApps);
      const appsWithDeviceUsage = eventAppsUtil.appsWithDeviceUsage(selectedApps);

      const connectionTime = overviewDashboardUtil.getConnectionTime(
        data.filter((d) => appsWithConnectionTime.some((a) => a.appType === d.appType))
      );
      const deviceUsage = overviewDashboardUtil.getDeviceUsage(
        data.filter((d) => appsWithDeviceUsage.some((a) => a.appType === d.appType))
      );

      dispatch(setConnectionTimeEvents(connectionTime));
      dispatch(setDeviceUsageEvents(deviceUsage));
    }
  },
  [rumEvents.ADVANCED_DIAGNOSTIC_COMPLETED]: {
    chartNames: [chartNames.AD_STATISTICS, chartNames.DIAGNOSTICS_PERFORMANCE],
    deviceFilterPatternFn: dashboardUtil.getFilterPatternByTypeName,
    group: dashboardGroups.DIAGNOSTIC,
    saveDataFn: (data) => {
      const selectedApps = appsUtil.getSelectedApps();

      const appsWithADStatistic = eventAppsUtil.appsWithADStatistic(selectedApps);
      const appsWithADPerformance = eventAppsUtil.appsWithADPerformance(selectedApps);

      const diagnosticsPerformace = diagnosticDashboardUtil.getDiagnosticsPerformance(
        data.filter((d) => appsWithADPerformance.some((a) => a.appType === d.appType))
      );
      const { result: diagnosticsStatistics, totalADCount } =
        diagnosticDashboardUtil.getDiagnosticsStatistics(
          data.filter((d) => appsWithADStatistic.some((a) => a.appType === d.appType))
        );

      dispatch(
        setDiagnosticsEvents({
          data,
          statistics: diagnosticsStatistics,
          performance: diagnosticsPerformace,
          total: totalADCount
        })
      );
    }
  },
  [rumEvents.ADVANCED_DIAGNOSTIC_PROCESS]: {
    chartNames: [chartNames.AD_STATISTICS, chartNames.DIAGNOSTICS_ERRORS_READING_PERFOMANCE],
    deviceFilterPatternFn: dashboardUtil.getFilterPatternByTypeName,
    group: dashboardGroups.DIAGNOSTIC,
    saveDataFn: (data) => {
      const selectedApps = appsUtil.getSelectedApps();

      const appsWithADStatistic = eventAppsUtil.appsWithADStatistic(selectedApps);
      const appsWithADError = eventAppsUtil.appsWithADError(selectedApps);

      const diagnosticsPerformace = diagnosticDashboardUtil.getDiagnosticsErrorsReadingPerfomance(
        data.filter((d) => appsWithADError.some((a) => a.appType === d.appType))
      );
      const { result: diagnosticsStatistics, totalADCount } =
        diagnosticDashboardUtil.getDiagnosticsStatistics(
          data.filter((d) => appsWithADStatistic.some((a) => a.appType === d.appType))
        );

      dispatch(
        setDiagnosticsErrorsReadingPerfomanceEvents({
          data,
          statistics: diagnosticsStatistics,
          performance: diagnosticsPerformace,
          total: totalADCount
        })
      );
    }
  },
  [rumEvents.FIRMWARE_UPDATE]: {
    chartNames: [chartNames.FWU_STATISTICS, chartNames.FWU_PERFORMANCE, chartNames.FWU_ERRORS],
    deviceFilterPatternFn: dashboardUtil.getFilterPatternByTypeName,
    group: dashboardGroups.FIRMWARE_UPDATE,
    saveDataFn: (data) => {
      const selectedApps = appsUtil.getSelectedApps();

      const appsWithFWUStatistic = eventAppsUtil.appsWithFWUStatistic(selectedApps);
      const appsWithFWUPerformance = eventAppsUtil.appsWithFWUPerformance(selectedApps);
      const appsWithFWUErrors = eventAppsUtil.appsWithFWUErrors(selectedApps);

      const fwuPerformace = firmwareDashboardUtil.getFwUpdatePerformance(
        data.filter((d) => appsWithFWUPerformance.some((a) => a.appType === d.appType))
      );
      const { result: fwuStatistics, totalFWUCount } = firmwareDashboardUtil.getFwUpdateStatistics(
        data.filter((d) => appsWithFWUStatistic.some((a) => a.appType === d.appType))
      );
      const fwuErrors = firmwareDashboardUtil.getFwUpdateErrors(
        data.filter((d) => appsWithFWUErrors.some((a) => a.appType === d.appType))
      );

      dispatch(
        setFwuEvents({
          data,
          statistics: fwuStatistics,
          performance: fwuPerformace,
          errors: fwuErrors,
          total: totalFWUCount
        })
      );
    }
  },
  [rumEvents.FIRMWARE_UPDATE_DEVICE_RECONNECTION]: {
    chartNames: [chartNames.RECONNECTION_TIME_DURING_FWU],
    deviceFilterPatternFn: dashboardUtil.getFilterPatternByTypeName,
    group: dashboardGroups.FIRMWARE_UPDATE,
    saveDataFn: (data) => {
      const selectedApps = appsUtil.getSelectedApps();

      const appsWithFWUReconnectionTime = eventAppsUtil.appsWithFWUReconnectionTime(selectedApps);

      const reconnectionTime = firmwareDashboardUtil.getReconnectionTimeDuringFWU(
        data.filter((d) => appsWithFWUReconnectionTime.some((a) => a.appType === d.appType))
      );
      dispatch(setReconnectionTimeEvents(reconnectionTime));
    }
  },
  [rumEvents.SETTINGS]: {
    chartNames: [chartNames.SETTING_USAGE, chartNames.SETTING_PERFOMANCE],
    deviceFilterPatternFn: dashboardUtil.getFilterPatternByDeviceName,
    group: dashboardGroups.DEVICE_SETTINGS,
    saveDataFn: (data) => {
      const selectedApps = appsUtil.getSelectedApps();

      const appsWithSettingUsage = eventAppsUtil.appsWithSettingUsage(selectedApps);
      const appsWithSettingPerformance = eventAppsUtil.appsWithSettingPerformance(selectedApps);

      const statistics = settingsDashboardUtil.getSettingUsage(
        data.filter((d) => appsWithSettingUsage.some((a) => a.appType === d.appType))
      );
      const availableSettings = statistics
        ?.filter((setting) => setting.metrics?.successful || setting.metrics?.failed)
        ?.map((setting) =>
          Object.keys(settingNames).find((key) => settingNames[key] === setting.setting)
        );

      const selectedSetting = availableSettings[0];

      const performance = settingsDashboardUtil.getSettingPerfomance(
        data.filter((d) => appsWithSettingPerformance.some((a) => a.appType === d.appType)),
        selectedSetting
      );

      dispatch(
        setSettingEvents({
          statistics,
          performance,
          data,
          availableSettings,
          selectedSetting
        })
      );
    }
  },
  [rumEvents.BATTERY_HEALTH]: {
    chartNames: [chartNames.BATTERY_HEALTH_STATISTICS, chartNames.BATTERY_HEALTH_PERFORMANCE],
    deviceFilterPatternFn: dashboardUtil.getFilterPatternByDeviceName,
    group: dashboardGroups.BATTERY_HEALTH,
    saveDataFn: (data) => {
      const selectedApps = appsUtil.getSelectedApps();

      const appsWithHealthStatistics = eventAppsUtil.appsWithHealthStatistics(selectedApps);
      const appsWithHealthPerformance = eventAppsUtil.appsWithHealthPerformance(selectedApps);

      const batteryHealthPerformace = batteryHealthDashboardUtil.getBatteryHealthPerformance(
        data.filter((d) => appsWithHealthPerformance.some((a) => a.appType === d.appType))
      );
      const batteryHealthStatistics = batteryHealthDashboardUtil.getBatteryHealthStatistics(
        data.filter((d) => appsWithHealthStatistics.some((a) => a.appType === d.appType))
      );

      dispatch(
        setBatteryHealthEvents({
          data,
          statistics: batteryHealthStatistics,
          performance: batteryHealthPerformace
        })
      );
    }
  }
};

const fetchMarketEventsConfig = {
  [rumEvents.CONNECT_DEVICE]: {
    chartNames: [chartNames.IQOS_DEVICE_CONNECTIONS_COUNT, chartNames.BROWSER, chartNames.OS],
    deviceFilterPatternFn: dashboardUtil.getFilterPatternByDeviceName,
    group: dashboardGroups.OVERVIEW,
    saveDataFn: (data) => {
      const market = selectSelectedMarket()(getState());

      const selectedApps = appsUtil.getSelectedApps();

      const appsWithDeviceConnectionsCount =
        eventAppsUtil.appsWithDeviceConnectionsCount(selectedApps);
      const appsWithConnectionsByBrowser = eventAppsUtil.appsWithConnectionsByBrowser(selectedApps);
      const appsWithConnectionsByOS = eventAppsUtil.appsWithConnectionsByOS(selectedApps);

      const { result: statistics, totalDeviceConnectionsCount: total } =
        appsWithDeviceConnectionsCount.length > 1
          ? overviewDashboardUtil.getDeviceConnectionsCountByApp(
              data.filter((d) =>
                appsWithDeviceConnectionsCount.some((a) => a.appType === d.appType)
              )
            )
          : !market || market.length !== 1
          ? overviewDashboardUtil.getDeviceConnectionsCountByMarket(
              data.filter((d) =>
                appsWithDeviceConnectionsCount.some((a) => a.appType === d.appType)
              )
            )
          : overviewDashboardUtil.getDeviceConnectionsCountByLocation(
              data.filter((d) =>
                appsWithDeviceConnectionsCount.some((a) => a.appType === d.appType)
              )
            );

      const { result: browserStatistic, totalDeviceConnectionsCount: browserTotal } =
        browserAndOSUtil.getDeviceConnectionsCountByBrowser(
          data.filter((d) => appsWithConnectionsByBrowser.some((a) => a.appType === d.appType))
        );

      const { result: osStatistics, totalDeviceConnectionsCount: osTotal } =
        browserAndOSUtil.getDeviceConnectionsCountByOS(
          data.filter((d) => appsWithConnectionsByOS.some((a) => a.appType === d.appType))
        );

      dispatch(setConnectionsByOS({ statistics: osStatistics, total: osTotal }));
      dispatch(setConnectionsByBrowser({ statistics: browserStatistic, total: browserTotal }));
      dispatch(setConnectionsByMarket({ statistics, total }));
    }
  },
  [rumEvents.ADVANCED_DIAGNOSTIC_COMPLETED]: {
    chartNames: [chartNames.AD_COUNT],
    deviceFilterPatternFn: dashboardUtil.getFilterPatternByTypeName,
    group: dashboardGroups.DIAGNOSTIC,
    saveDataFn: (data) => {
      const market = selectSelectedMarket()(getState());

      const selectedApps = appsUtil.getSelectedApps();

      const appsWithCompletedDiagnostic = eventAppsUtil.appsWithCompletedDiagnostic(selectedApps);

      const statisticsByMarket =
        appsWithCompletedDiagnostic?.length > 1
          ? diagnosticDashboardUtil.getDiagnosticCountByAppType(
              data.filter((d) => appsWithCompletedDiagnostic.some((a) => a.appType === d.appType))
            )
          : !market || market.length !== 1
          ? diagnosticDashboardUtil.getDiagnosticCountByMarket(
              data.filter((d) => appsWithCompletedDiagnostic.some((a) => a.appType === d.appType))
            )
          : diagnosticDashboardUtil.getDiagnosticCountByLocation(
              data.filter((d) => appsWithCompletedDiagnostic.some((a) => a.appType === d.appType))
            );

      dispatch(setDiagnosticsByMarket({ data, statistics: statisticsByMarket }));
    }
  },
  [rumEvents.FIRMWARE_UPDATE]: {
    chartNames: [chartNames.FWU_COUNT],
    deviceFilterPatternFn: dashboardUtil.getFilterPatternByTypeName,
    group: dashboardGroups.FIRMWARE_UPDATE,
    saveDataFn: (data) => {
      const market = selectSelectedMarket()(getState());

      const selectedApps = appsUtil.getSelectedApps();

      const appsWithFWUCount = eventAppsUtil.appsWithFWUCount(selectedApps);

      const statisticsByMarket =
        appsWithFWUCount?.length > 1
          ? firmwareDashboardUtil.getFwCountByAppType(
              data.filter((d) => appsWithFWUCount.some((a) => a.appType === d.appType))
            )
          : !market || market.length !== 1
          ? firmwareDashboardUtil.getFwCountByMarket(
              data.filter((d) => appsWithFWUCount.some((a) => a.appType === d.appType))
            )
          : firmwareDashboardUtil.getFwCountByLocation(
              data.filter((d) => appsWithFWUCount.some((a) => a.appType === d.appType))
            );

      dispatch(setFwuByMarket({ data, statistics: statisticsByMarket }));
    }
  },
  [rumEvents.ADVANCED_DIAGNOSTIC_PROCESS]: {
    chartNames: [chartNames.AD_STATISTICS, chartNames.DIAGNOSTICS_ERRORS_READING_PERFOMANCE],
    deviceFilterPatternFn: dashboardUtil.getFilterPatternByTypeName,
    group: dashboardGroups.DIAGNOSTIC,
    saveDataFn: (data) => {
      const selectedApps = appsUtil.getSelectedApps();

      const appsWithADStatistic = eventAppsUtil.appsWithADStatistic(selectedApps);
      const appsWithADError = eventAppsUtil.appsWithADError(selectedApps);

      const diagnosticsPerformace = diagnosticDashboardUtil.getDiagnosticsErrorsReadingPerfomance(
        data.filter((d) => appsWithADError.some((a) => a.appType === d.appType))
      );
      const { result: diagnosticsStatistics, totalADCount } =
        diagnosticDashboardUtil.getDiagnosticsStatistics(
          data.filter((d) => appsWithADStatistic.some((a) => a.appType === d.appType))
        );

      dispatch(
        setDiagnosticsErrorsReadingPerfomanceEvents({
          data,
          statistics: diagnosticsStatistics,
          performance: diagnosticsPerformace,
          total: totalADCount
        })
      );
    }
  }
};

export { fetchEventsConfig, fetchMarketEventsConfig };
